import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Store de cache de respostas de API com tempo de expiração (TTL).
 *
 * Cada entrada é identificada por uma chave — normalmente a URL completa da
 * requisição, incluindo os parâmetros — e expira automaticamente após o TTL.
 *
 * Persistido em `sessionStorage`: o cache sobrevive a um F5 e à navegação
 * entre telas, mas morre ao fechar a aba. Como guarda dados sensíveis do
 * usuário logado, `limpar()` é chamado no logout.
 *
 * Uso:
 *   const cache = useCacheRequisicoesStore();
 *
 *   const dados = await cache.obterOuBuscar(
 *       `/api/v1/dashboard/doa-por-ano?ano=${ano}`,
 *       () => getDados(url),
 *   );
 *
 *   // Forçar dado fresco (botão "Atualizar"):
 *   cache.invalidar('/api/v1/dashboard');
 */
export const TTL_PADRAO_MINUTOS = 30;

// Requisições em andamento, por chave. Fica fora do state porque Promise não é
// serializável — e o cache é persistido. Serve para evitar que duas chamadas
// simultâneas à mesma URL (ex: o onMounted e um watch disparando juntos)
// resultem em duas idas ao servidor.
const requisicoesEmVoo = new Map();

export const useCacheRequisicoesStore = defineStore(
    'cacheRequisicoes',
    () => {
        // { [chave]: { dados, expiraEm } }
        const cache = ref({});

        /**
         * Retorna os dados em cache, ou `null` se não houver ou já tiver expirado.
         * @param {string} chave
         */
        const obter = (chave) => {
            const item = cache.value[chave];
            if (!item) return null;

            if (Date.now() > item.expiraEm) {
                delete cache.value[chave];
                return null;
            }

            return item.dados;
        };

        /**
         * Guarda os dados em cache.
         * @param {string} chave
         * @param {*} dados
         * @param {number} ttlMinutos - Tempo de vida da entrada, em minutos
         */
        const salvar = (chave, dados, ttlMinutos = TTL_PADRAO_MINUTOS) => {
            cache.value[chave] = {
                dados,
                expiraEm: Date.now() + ttlMinutos * 60 * 1000,
            };
        };

        /**
         * Remove todas as entradas cuja chave começa com o prefixo informado.
         * @param {string} prefixo - Ex: '/api/v1/dashboard'
         */
        const invalidar = (prefixo) => {
            Object.keys(cache.value)
                .filter((chave) => chave.startsWith(prefixo))
                .forEach((chave) => delete cache.value[chave]);
        };

        /**
         * Esvazia o cache por completo. Chamado no logout.
         */
        const limpar = () => {
            cache.value = {};
            requisicoesEmVoo.clear();
        };

        /**
         * Devolve o valor em cache ou executa `buscador` e guarda o resultado.
         *
         * @param {string} chave - Identificador da requisição (use a URL completa)
         * @param {() => Promise<*>} buscador - Função que faz a requisição de fato
         * @param {object} [opcoes]
         * @param {number} [opcoes.ttlMinutos] - TTL da entrada
         * @param {boolean} [opcoes.forcar] - Ignora o cache e busca no servidor
         * @returns {Promise<*>}
         */
        const obterOuBuscar = async (chave, buscador, opcoes = {}) => {
            const { ttlMinutos = TTL_PADRAO_MINUTOS, forcar = false } = opcoes;

            if (!forcar) {
                const emCache = obter(chave);
                if (emCache !== null) return emCache;

                const emVoo = requisicoesEmVoo.get(chave);
                if (emVoo) return emVoo;
            }

            const promise = buscador()
                .then((dados) => {
                    salvar(chave, dados, ttlMinutos);
                    return dados;
                })
                .finally(() => {
                    requisicoesEmVoo.delete(chave);
                });

            requisicoesEmVoo.set(chave, promise);

            return promise;
        };

        return {
            cache,
            obter,
            salvar,
            invalidar,
            limpar,
            obterOuBuscar,
        };
    },
    { persist: { storage: sessionStorage } },
);
